import type { NavigateFunction } from 'react-router-dom';
import type { Action, Group, Role, User } from '@gcv/shared';
import { isBankUser, isDispensaryUser, isGcvUser, OrganizationRoleResolver } from '@gcv/shared';

import { UsersApi } from 'api';
import type { UserWithType } from 'domain/types';
import { UserType } from 'domain/types';
import { getCrbDispensaryStore } from 'stores/CrbDispensaryStore';
import { getGcvAdminStore } from 'stores/GcvAdminStore';
import { getSnackbarStore } from 'stores/SnackBarStore';
import { initializePendo } from 'third-party-integrations/pendo';
import { deleteRedirectUrl, getRedirectUrl } from 'util/auth0-storage.util';
import { DateTimeHelpers } from 'util/dateTime.util';

/**
 * Redirect user to the desired path.
 *
 * @param pathNameGuard substring that must be found in current path.
 * @param defaultPathName path to use when none is stored.
 * @param history React history object.
 */
export const redirect = (
  pathNameGuard: string,
  defaultPathName: string,
  navigate: NavigateFunction
): void => {
  const storedPathName = getRedirectUrl(true);

  if (storedPathName === window.location.pathname) {
    return;
  }

  if (storedPathName?.includes(pathNameGuard)) {
    navigate(storedPathName);
    deleteRedirectUrl();
  } else {
    const pathName = window.location.pathname.includes(pathNameGuard)
      ? window.location.pathname + window.location.search
      : defaultPathName;

    navigate(pathName);
  }
};

const getRolesForPath = (path: string): Role[] => {
  // If current path includes the key, return roles
  // Update as you update the regex checks below
  // const pathRoles: Record<string, Role[]> = {
  //   'provider-onboarding': ['dispensary_account_owner'],
  //   'login/support': ['gcv_customer_support']
  // };
  const roles: Role[] = [];
  if (path.match(/provider-onboarding/g)) {
    roles.push('dispensary_account_owner');
  } else if (path.match(/login\/support/g)) {
    roles.push('gcv_customer_support');
  }
  return roles;
};

const getActionForPath = (path: string): Action | undefined => {
  // If current path includes the key, return action
  // Update as you update the regex checks
  // const pathActions: Record<string, Action> = {
  //   'dashboard/deposit': 'deposit_view_all',
  //   marketplace: 'organizational_details_update',
  //   'my-providers': 'organizational_details_update',
  //   company: 'organizational_details_update'
  // };
  if (path.match(/dashboard\/deposit/g)) {
    return 'deposit_view_all';
  } else if (path.match(/marketplace|my-providers|company/g)) {
    return 'organizational_details_update';
  } else if (path.match(/users/g)) {
    return 'user_add';
  }
  return;
};

export const userHasRole = (orgGroups: Group[], user: User, path: string): boolean => {
  const roles = getRolesForPath(path);
  const roleResolver: OrganizationRoleResolver = new OrganizationRoleResolver();
  let hasRole = true;

  if (roles) {
    roles.forEach((role) => {
      hasRole = hasRole && roleResolver.userHasRole(orgGroups, user, role);
    });
  }

  return hasRole;
};

export const userCanDoAction = (orgGroups: Group[], user: User, path: string): boolean => {
  const action = getActionForPath(path);
  const roleResolver: OrganizationRoleResolver = new OrganizationRoleResolver();
  const hasAction = action ? roleResolver.userCanDoAction(orgGroups, user, action) : true;

  return hasAction;
};

export const initializeLoggedInUser = async (
  navigate: NavigateFunction,
  user: UserWithType | undefined,
  setIsLoading: (loading: boolean) => void
) => {
  const usersApi = new UsersApi();

  if (!user || user?.invitation_status === 'archived') return;

  try {
    initializePendo(user);
    await usersApi.updateUserIdentification(user?.id, {
      lastLogin: DateTimeHelpers.getUtcIsoString(),
      invitation_status: user?.invitation_status === 'pending' ? 'accepted' : undefined
    });
    // This sets the users MFA requirements on the BE
    await usersApi.checkMfaRequired(user?.email);

    if (user?.role === 'gcv_customer_support') {
      navigate('/login/support');
      return;
    }

    switch (user?.type) {
      case UserType.Bank: {
        await initFi(navigate);
        break;
      }
      case UserType.Dispensary: {
        await initCrb(navigate, user, setIsLoading);
        break;
      }
      case UserType.GCV: {
        await initGcv(navigate, setIsLoading);
        break;
      }
    }
  } catch (e) {
    getSnackbarStore().showErrorSnackbarMessage(
      'Something went wrong! Please contact support if the problem persists.'
    );
    navigate('/error');
  }
};

export const getUserType = (user: User): 'bank' | 'dispensary' | 'gcv' => {
  if (isGcvUser(user)) {
    if (user?.role === 'gcv_customer_support') {
      return user?.companies[0].companyType;
    } else {
      return 'gcv';
    }
  } else if (isBankUser(user)) {
    return 'bank';
  } else if (isDispensaryUser(user)) {
    return 'dispensary';
  } else {
    throw new Error(`User ${user?.id} type is not defined: ${user?.role}`);
  }
};

export const initGcv = async (navigate: NavigateFunction, setIsLoading: (loading: boolean) => void) => {
  try {
    setIsLoading(true);
    const gcvAdminStore = getGcvAdminStore();
    await gcvAdminStore.loadStore();
    redirect('/secure/gcv/', '/secure/gcv/dashboard', navigate);
  } catch (e) {
    throw e;
  } finally {
    setIsLoading(false);
  }
};

export const initFi = (navigate: NavigateFunction) =>
  redirect('/secure/fi/', '/secure/fi/dashboard', navigate);

export const initCrb = async (
  navigate: NavigateFunction,
  user: UserWithType | undefined,
  setIsLoading: (loading: boolean) => void
) => {
  if (!user || !user?.id) return;

  try {
    setIsLoading(true);
    const dispensaryStore = getCrbDispensaryStore();

    if (!user?.companies?.length && user?.marketing_user_in_progress) {
      redirect('/secure/crb/', '/secure/crb/welcome', navigate);
      return;
    }

    await dispensaryStore.loadDispensaries(user.id);

    if (dispensaryStore.isLoaded && dispensaryStore.invitedDispensaries.length !== 0) {
      navigate('/secure/crb/connect-org');
    }

    /**
     * Check the URL to see if it's a deep link to a specific crb and use that as the initial CRB ex: /secure/crb/${crbId}/...
     * Use the redirect URL that we store in the sessionStorage so that this works for both cases of user logging in + user already logged in
     */
    let urlCrbId;
    const storedPathName = getRedirectUrl();
    if (storedPathName) {
      const regex = /crb\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/;
      const match = storedPathName.match(regex);

      if (match) {
        urlCrbId = match[1];
      }
    }

    //check local storage to see if MUO user had a previous selected dispensary
    const previousSelectedDispensary = window.sessionStorage.getItem('currentDispensaryId');
    const isPreviouslySelectedDispensary = Boolean(
      previousSelectedDispensary && user?.companies.some(({ id }) => id === previousSelectedDispensary)
    );
    const currentDispensary = urlCrbId
      ? urlCrbId
      : isPreviouslySelectedDispensary
      ? previousSelectedDispensary
      : user?.companies[0].id;

    await dispensaryStore.setCurrentDispensary(currentDispensary ?? '');

    if (!user?.termsOfService && user?.initial_user && user?.role !== 'gcv_customer_support') {
      navigate('/tos');
    } else {
      redirect('/secure/crb/', '/secure/crb/dashboard', navigate);
    }
  } catch (e) {
    throw e;
  } finally {
    setIsLoading(false);
  }
};
